import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../Common/authApi';
import { handleApiError } from '../Common/authErrorHandler';
import useAxiosHelper from '../Common/AxiosHelper';
import { registerUser, DEFAULT_REGISTRATION_SETTINGS } from '../Common/registerApi';
import { ApiPaths } from '../Config/ApiPath';

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const { AxiosPost, AxiosPostPublic } = useAxiosHelper();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = authApi.getToken();
    if (storedToken) setToken(storedToken);
    setLoading(false);
  }, []);

  const login = useCallback(async (username, password) => {
    try {
      const data = await AxiosPost(ApiPaths.login, { username, password });
      const authToken = data?.token ?? data?.data?.token;
      const userData = data?.user ?? data?.User ?? data?.data?.user ?? data?.data?.User;
      if (authToken) {
        authApi.setToken(authToken);
        setToken(authToken);
        setUser(userData ?? data);
        if (userData && userData.uid != null) {
          localStorage.setItem('userId', String(userData.uid));
        }
        return { success: true };
      }
      return { success: false, error: data?.message || 'Unexpected error occurred' };
    } catch (error) {
      const errMsg = error?.response?.data?.message || error?.message;
      return {
        success: false,
        error: handleApiError({ code: error?.response?.status, message: errMsg }) || errMsg || 'Login failed',
      };
    }
  }, [AxiosPost]);

  const register = useCallback(async (form, settings = DEFAULT_REGISTRATION_SETTINGS) => {
    try {
      /* register is a public endpoint — no Authorization header */
      const data = await registerUser(AxiosPostPublic, form, settings);
      const authToken = data?.token ?? data?.data?.token;
      const userDataFromApi = data?.User ?? data?.user ?? data?.data?.User ?? data?.data?.user;
      if (authToken) {
        authApi.setToken(authToken);
        setToken(authToken);
        setUser(userDataFromApi ?? data);
        if (userDataFromApi && userDataFromApi.uid != null) {
          localStorage.setItem('userId', String(userDataFromApi.uid));
        }
        return { success: true, user: userDataFromApi ?? data };
      }
      return { success: false, error: data?.message || 'Unexpected error occurred' };
    } catch (error) {
      const errMsg = error?.response?.data?.message || error?.message;
      return {
        success: false,
        error: handleApiError({ code: error?.response?.status, message: errMsg }) || errMsg || 'Registration failed',
      };
    }
  }, [AxiosPostPublic]);

  const logout = useCallback(() => {
    authApi.removeToken();
    localStorage.removeItem('userId');
    setToken(null);
    setUser(null);
  }, []);

  /**
   * Login using a token from URL (e.g. ?token=JWT). Validates by calling profile API.
   * Call after validating token with get_profile; pass token and profile/user data.
   */
  const loginWithToken = useCallback((authToken, userData) => {
    if (!authToken) return { success: false, error: 'Token is required' };
    authApi.setToken(authToken);
    setToken(authToken);
    setUser(userData ?? null);
    if (userData && (userData.uid != null || userData.u_id != null)) {
      localStorage.setItem('userId', String(userData.uid ?? userData.u_id));
    }
    return { success: true };
  }, []);

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    loginWithToken,
    isAuthenticated: !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
